/**
 * AnswerDisplay — Compact read-only display of a saved checklist answer
 *
 * Shows the answer value formatted by type, source badge (Manual vs AI),
 * timestamp, and an optional edit button (wired in phase 3).
 */

import { useState } from "react";
import { Pencil, ChevronDown, ChevronUp, Bot, User } from "lucide-react";
import type { ChecklistItemAnswer } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

// ─── Props ──────────────────────────────────────────────────────────────────

interface AnswerDisplayProps {
  answer: ChecklistItemAnswer;
  onEdit?: () => void;
}

// ─── Answer Value Formatters ────────────────────────────────────────────────

function formatAnswerValue(
  answer: ChecklistItemAnswer,
  tCompliant: string,
  tNonCompliant: string,
  tNa: string,
): {
  text: string;
  dotColor?: string;
  textClassName?: string;
} {
  const questionType = answer.question.type;

  if (questionType === "yes_no") {
    if (answer.answer === "not_applicable") {
      return {
        text: tNa,
        textClassName: "text-[12px] text-muted italic",
      };
    }

    const isYes = answer.answer === true || answer.answer === "true";
    return {
      text: isYes ? tCompliant : tNonCompliant,
      dotColor: isYes ? "bg-green-500" : "bg-red-500",
    };
  }

  if (questionType === "date") {
    const ts =
      typeof answer.answer === "number" ? answer.answer : Number(answer.answer);
    if (!Number.isNaN(ts)) {
      const formatted = new Date(ts).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return { text: formatted };
    }
    return { text: String(answer.answer) };
  }

  // text, document, selection
  return { text: String(answer.answer) };
}

// ─── Source Documents Excerpt ────────────────────────────────────────────────

function SourceDocumentsSection({ answer }: { answer: ChecklistItemAnswer }) {
  const { t } = useTranslation("audits");
  const [isOpen, setIsOpen] = useState(false);
  const docs = answer.sourceDocuments;

  if (!docs || docs.length === 0 || answer.source !== "ai_extracted") {
    return null;
  }

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-secondary transition-colors"
      >
        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {docs.length}{" "}
        {t("checklist.answers.sourceDocuments", { count: docs.length })}
      </button>
      {isOpen && (
        <div className="mt-1.5 space-y-1.5">
          {docs.map((doc, i) => (
            <div
              key={`${doc.documentId}-${i}`}
              className="rounded border border-border/60 bg-card/50 px-2.5 py-1.5"
            >
              <p className="text-[11px] text-secondary italic leading-relaxed">
                &ldquo;{doc.excerpt}&rdquo;
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function AnswerDisplay({ answer, onEdit }: AnswerDisplayProps) {
  const { t } = useTranslation(["audits", "common"]);
  const { text, dotColor, textClassName } = formatAnswerValue(
    answer,
    t("checklist.answers.compliant"),
    t("checklist.answers.nonCompliant"),
    t("common:actions.na", "N/A"),
  );
  const timestamp = new Date(answer.answeredAt).toLocaleDateString();
  const isAI = answer.source === "ai_extracted";
  const SourceIcon = isAI ? Bot : User;
  const sourceLabel = isAI ? "KI" : t("checklist.answers.manual");

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5 space-y-2">
      {/* Top row: answer value + source/timestamp top-right */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {dotColor && (
            <span className={`size-2 rounded-full shrink-0 ${dotColor}`} />
          )}
          <span
            className={textClassName ?? "text-[13px] font-medium text-primary"}
          >
            {text}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-muted shrink-0">
          <SourceIcon size={10} className="shrink-0" />
          <span>{sourceLabel}</span>
          <span>&middot;</span>
          <span>{t("checklist.answers.answered", { timestamp })}</span>
        </div>
      </div>

      {/* Notes — aligned under text, not under dot */}
      {answer.notes && (
        <p className="text-[13px] text-muted leading-relaxed italic pl-4">
          {answer.notes}
        </p>
      )}

      {/* Collapsible source documents */}
      <SourceDocumentsSection answer={answer} />

      {/* Edit button — bottom-right */}
      {onEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-0.5 text-[11px] font-medium text-accent hover:bg-accent/20 transition-colors"
          >
            <Pencil size={10} />
            {t("checklist.answers.edit")}
          </button>
        </div>
      )}
    </div>
  );
}
